<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatBytes } from 'shared/helpers/FileHelper';

import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';

const { attachments } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => attachments.value[0] || {});
const url = computed(() => attachment.value?.dataUrl || '');

const fileName = computed(() => {
  const u = url.value;
  if (!u) return t('CONVERSATION.UNKNOWN_FILE_TYPE');
  const raw = u.substring(u.lastIndexOf('/') + 1).split('?')[0];
  try {
    return decodeURIComponent(raw) || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  } catch (e) {
    return raw || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
});

const ext = computed(() => (fileName.value.split('.').pop() || '').toLowerCase());
const typeLabel = computed(() => (ext.value || 'FILE').toUpperCase());

const fileSize = computed(
  () => attachment.value?.fileSize || attachment.value?.file_size || 0
);

// SZ: mapa extensao -> icone custom (Figma 2026-05-28). Nao mapeado = generico.
const ICON_MAP = {
  pdf: 'sz-filetype-pdf',
  csv: 'sz-filetype-sheet',
  xls: 'sz-filetype-sheet',
  xlsx: 'sz-filetype-sheet',
  doc: 'sz-filetype-doc',
  docx: 'sz-filetype-doc',
};
const ICON_VERSION = '20260528a';
const iconSrc = computed(
  () => `/static/img/${ICON_MAP[ext.value] || 'sz-filetype-file'}.png?v=${ICON_VERSION}`
);

const isPdf = computed(() => ext.value === 'pdf');

// ── PDF preview (pdf.js lazy-load + cache) ──
const pdfCanvas = ref(null);
const pdfPaneRef = ref(null);
const pdfPages = ref(0);
const pdfRendering = ref(false);
const pdfFailed = ref(false);

const metaLine = computed(() => {
  const parts = [];
  if (isPdf.value && pdfPages.value > 0) {
    parts.push(`${pdfPages.value} ${pdfPages.value === 1 ? 'página' : 'páginas'}`);
  }
  parts.push(typeLabel.value);
  if (fileSize.value) parts.push(formatBytes(fileSize.value, 0));
  return parts.join(' • ');
});

const PDFJS_VER = '3.11.174';
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}`;

// SZ: reescreve o host da URL pro origin atual. O caminho do blob (signed key) eh
// identico em Tokyo/Brasa (DB compartilhado); o arquivo fisico fica no storage do host
// onde foi subido = o host que esta servindo a pagina. Isso faz o pdf.js/download
// buscarem same-origin no storage correto (resolve o split Tokyo/Brasa).
function sameOriginUrl(u) {
  if (!u) return u;
  try {
    const parsed = new URL(u, window.location.origin);
    return window.location.origin + parsed.pathname + parsed.search;
  } catch (e) {
    return u;
  }
}

async function ensurePdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${PDFJS_BASE}/pdf.min.js`;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
  }
  return window.pdfjsLib;
}

// cache global por attachment id: { pages, dataUrl }
const pdfCache = (window.__szPdfThumbCache = window.__szPdfThumbCache || new Map());

function paintCachedThumb(dataUrl) {
  const canvas = pdfCanvas.value;
  if (!canvas || !dataUrl) return;
  const img = new Image();
  img.onload = () => {
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext('2d').drawImage(img, 0, 0);
  };
  img.src = dataUrl;
}

async function renderPdf() {
  if (!isPdf.value || !url.value || pdfRendering.value) return;
  const key = attachment.value?.id || url.value;
  const cached = pdfCache.get(key);
  if (cached) {
    pdfPages.value = cached.pages;
    paintCachedThumb(cached.dataUrl);
    return;
  }
  pdfRendering.value = true;
  try {
    const pdfjs = await ensurePdfJs();
    if (!pdfjs) throw new Error('pdfjs nao carregou');
    const pdf = await pdfjs.getDocument(sameOriginUrl(url.value)).promise;
    pdfPages.value = pdf.numPages;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = 280 / base.width;
    const vp = page.getViewport({ scale });
    const canvas = pdfCanvas.value;
    if (!canvas) throw new Error('canvas ausente');
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    pdfCache.set(key, { pages: pdf.numPages, dataUrl: canvas.toDataURL('image/png') });
  } catch (e) {
    pdfFailed.value = true;
  } finally {
    pdfRendering.value = false;
  }
}

let io = null;
onMounted(() => {
  if (!isPdf.value) return;
  if ('IntersectionObserver' in window && pdfPaneRef.value) {
    io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        renderPdf();
        if (io) io.disconnect();
      }
    });
    io.observe(pdfPaneRef.value);
  } else {
    renderPdf();
  }
});
onBeforeUnmount(() => {
  if (io) io.disconnect();
});

// SZ_FILE_DL_FIX_20260604: window.open puro pode ser bloqueado (extensao/popup/
// security corporativa) e falhar EM SILENCIO. Usa <a download> nativo: um clique
// programatico em ancora e tratado como navegacao por gesto do usuario e raramente
// e bloqueado. Como sameOriginUrl deixa a URL same-origin, o atributo download
// dispara o save direto sem sair do app. Fallbacks garantem que o clique nunca
// falhe sem feedback (nova aba -> e, se bloqueada, navega na propria aba).
function openFile() {
  const href = url.value ? sameOriginUrl(url.value) : '';
  if (!href) return;
  try {
    const a = document.createElement('a');
    a.href = href;
    a.rel = 'noopener noreferrer';
    a.target = '_blank';
    if (fileName.value) a.download = fileName.value;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    if (!window.open(href, '_blank', 'noopener')) {
      window.location.href = href;
    }
  }
}
</script>

<template>
  <BaseBubble
    class="sz-file-bubble"
    :class="{ 'sz-file-bubble--pdf': isPdf && !pdfFailed }"
    data-bubble-name="file"
  >
    <div
      class="sz-file-card"
      role="button"
      tabindex="0"
      @click="openFile"
      @keydown.enter="openFile"
    >
      <!-- PDF preview pane -->
      <div
        v-if="isPdf && !pdfFailed"
        ref="pdfPaneRef"
        class="sz-file-pdf-pane"
      >
        <canvas ref="pdfCanvas" class="sz-file-pdf-canvas" />
        <div v-if="pdfRendering" class="sz-file-pdf-loading">
          <span class="sz-file-spinner" />
        </div>
      </div>
      <!-- info row (footer no PDF, inline nos demais) -->
      <div
        class="sz-file-row sz-file-row--footer"
        :class="{ 'sz-file-row--standalone': !(isPdf && !pdfFailed) }"
      >
        <img class="sz-file-icon" :src="iconSrc" :alt="typeLabel" />
        <div class="sz-file-info">
          <div class="sz-file-name">{{ fileName }}</div>
          <div class="sz-file-sub">{{ metaLine }}</div>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>

<style>
/* SZ: card de anexo de documento (Figma 2026-05-28) */
#app .sz-file-bubble {
  background: #DEEDE1 !important;
  padding: 4px !important;
  border-radius: 8px 0 8px 8px !important;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16) !important;
  max-width: 296px !important;
}
.dark #app .sz-file-bubble {
  background: #194121 !important;
  box-shadow: none !important;
}

.sz-file-card,
.sz-file-card:hover,
.sz-file-card:focus,
.sz-file-card .sz-file-name,
.sz-file-card .sz-file-sub {
  text-decoration: none !important;
}
.sz-file-card {
  display: block;
  color: inherit;
  width: 280px;
  max-width: 100%;
  cursor: pointer;
}

/* PDF preview pane (mostra topo da 1a pagina) */
.sz-file-pdf-pane {
  position: relative;
  width: 100%;
  height: 110px;
  background: #FFFFFF;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}
.sz-file-pdf-canvas {
  width: 100%;
  height: auto;
  display: block;
}
.sz-file-pdf-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-file-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(35, 109, 35, 0.25);
  border-top-color: #236D23;
  border-radius: 50%;
  animation: sz-file-spin 0.8s linear infinite;
}
@keyframes sz-file-spin { to { transform: rotate(360deg); } }

/* info row */
.sz-file-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
}
.sz-file-row--footer {
  background: #C2DCC7;
  border-radius: 0 0 8px 8px;
}
.dark .sz-file-row--footer {
  background: #082C10;
}
.sz-file-row--standalone {
  border-radius: 8px !important;
}

.sz-file-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  object-fit: contain;
}
.sz-file-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  flex: 1;
}
.sz-file-name {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: #000000;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.dark .sz-file-name { color: #FFFFFF; }
.sz-file-sub {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  font-size: 10px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.64);
  margin-top: 2px;
}
.dark .sz-file-sub { color: rgba(255, 255, 255, 0.64); }

/* timestamp (MessageMeta vem do Base.vue) no card de file */
#app .sz-file-bubble .message-meta {
  margin-top: 4px !important;
  padding: 0 4px !important;
  color: #000000 !important;
}
.dark #app .sz-file-bubble .message-meta {
  color: #7AA17C !important;
}
#app .sz-file-bubble .message-meta time { color: inherit !important; }

/* SZ: variante INBOUND (arquivo RECEBIDO). As regras acima são o estilo
   outbound/enviado (verde, rabicho à direita); aqui sobrescrevemos só quando a
   orientação é left (Base.vue aplica .left-bubble) + rabicho à esquerda.
   Claro: card BRANCO (estilo recebido WhatsApp). Escuro: neutro do tema (slate-4). */
#app .sz-file-bubble.left-bubble {
  background: #FFFFFF !important;
  border-radius: 0 8px 8px 8px !important;
}
#app .sz-file-bubble.left-bubble .sz-file-row--footer {
  background: #FFFFFF !important;
}
.dark #app .sz-file-bubble.left-bubble {
  background: rgb(var(--slate-4) / 1) !important;
}
.dark #app .sz-file-bubble.left-bubble .sz-file-row--footer {
  background: rgb(var(--slate-5) / 1) !important;
}
#app .sz-file-bubble.left-bubble .message-meta {
  color: rgb(var(--slate-11) / 1) !important;
}
</style>